import { html, type TemplateResult } from 'lit';
import { unsafeSVG } from 'lit/directives/unsafe-svg.js';
import { until } from 'lit/directives/until.js';

/**
 * Phosphor Icons (MIT, https://phosphoricons.com), style "fill" only.
 * Stored like other `affine-icon`s, with the name `ph:<icon-name>`.
 * The data (`fill.json`, generated from `@phosphor-icons/core`) is loaded lazily.
 */
export const PHOSPHOR_PREFIX = 'ph:';

export type PhosphorIcon = {
  /** Icon name including the `ph:` prefix. */
  name: string;
  keywords: string[];
  /** Inner markup of the 256x256 SVG (paths only). */
  svg: string;
};

type Row = [name: string, keywords: string, svg: string];

let loading: Promise<PhosphorIcon[]> | undefined;
let byName: Map<string, PhosphorIcon> | undefined;

export const isPhosphorIconName = (name: string | undefined) =>
  !!name && name.startsWith(PHOSPHOR_PREFIX);

export const loadPhosphorIcons = (): Promise<PhosphorIcon[]> => {
  loading ??= import('./fill.json').then(module => {
    const icons = (module.default as Row[]).map(
      ([name, keywords, svg]): PhosphorIcon => ({
        name: PHOSPHOR_PREFIX + name,
        keywords: keywords.split(' '),
        svg,
      })
    );
    byName = new Map(icons.map(icon => [icon.name, icon]));
    return icons;
  });
  return loading;
};

/** Synchronous lookup, only available after `loadPhosphorIcons` resolved. */
export const getLoadedPhosphorIconSvg = (name: string) =>
  byName?.get(name)?.svg;

export const loadPhosphorIconSvg = async (name: string) => {
  await loadPhosphorIcons();
  return getLoadedPhosphorIconSvg(name);
};

export const renderPhosphorIconSvg = (svg: string, style = ''): TemplateResult =>
  html`<svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 256 256"
    width="1em"
    height="1em"
    fill="currentColor"
    style=${style}
  >
    ${unsafeSVG(svg)}
  </svg>`;

/** Lit template for a `ph:` icon; empty until the data is loaded. */
export const phosphorIconLit = (name: string, style = ''): TemplateResult => {
  const loaded = getLoadedPhosphorIconSvg(name);
  if (loaded) {
    return renderPhosphorIconSvg(loaded, style);
  }
  return html`${until(
    loadPhosphorIconSvg(name).then(svg =>
      svg ? renderPhosphorIconSvg(svg, style) : html``
    ),
    html``
  )}`;
};
